import { Autocomplete, Box, TextField } from '@mui/material';
import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormTextField } from 'src/components/form';
import InputMask from 'src/components/InputMask';
import { Row, Section } from 'src/components/layout';
import { FormFields, STATE_OPTIONS } from 'src/constants';
import { dataTestIds } from 'src/constants/data-test-ids';
import { emailRegex, isPhoneNumberValid, isPostalCodeValid, REQUIRED_FIELD_ERROR_MESSAGE } from 'utils';

const contact = FormFields.patientContactInformation;

import { useTranslation } from 'react-i18next';

export const ContactContainer: FC<{ isLoading: boolean }> = ({ isLoading }) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();

  return (
    <Section title={t('patientProfile.sections.contactInformation')}>
      <Row label={t('patientProfile.fields.streetAddress')} inputId={contact.streetAddress.key} required>
        <FormTextField
          name={contact.streetAddress.key}
          data-testid={dataTestIds.contactInformationContainer.streetAddress}
          control={control}
          disabled={isLoading}
          InputProps={{ readOnly: true }}
          rules={{ required: REQUIRED_FIELD_ERROR_MESSAGE }}
          id={contact.streetAddress.key}
        />
      </Row>
      <Row label={t('patientProfile.fields.addressLine2')} inputId={contact.addressLine2.key}>
        <FormTextField
          name={contact.addressLine2.key}
          control={control}
          id={contact.addressLine2.key}
          disabled={isLoading}
          InputProps={{ readOnly: true }}
          data-testid={dataTestIds.contactInformationContainer.addressLineOptional}
        />
      </Row>
      <Row label={t('patientProfile.fields.cityStateZip')} required>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormTextField
            name={contact.city.key}
            control={control}
            disabled={isLoading}
            InputProps={{ readOnly: true }}
            rules={{
              required: REQUIRED_FIELD_ERROR_MESSAGE,
            }}
            data-testid={dataTestIds.contactInformationContainer.city}
          />
          <Controller
            name={contact.state.key}
            control={control}
            rules={{
              required: REQUIRED_FIELD_ERROR_MESSAGE,
            }}
            render={({ field: { value }, fieldState: { error } }) => {
              return (
                <Autocomplete
                  options={STATE_OPTIONS.map((option) => option.value)}
                  value={value ?? ''}
                  data-testid={dataTestIds.contactInformationContainer.state}
                  readOnly={true}
                  onChange={(_, newValue) => {
                    if (newValue) {
                      setValue(contact.state.key, newValue);
                    } else {
                      setValue(contact.state.key, '');
                    }
                  }}
                  disableClearable
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      error={!!error}
                      required
                      helperText={error?.message}
                      disabled={isLoading}
                    />
                  )}
                />
              );
            }}
          />
          <FormTextField
            name={contact.zip.key}
            control={control}
            disabled={isLoading}
            InputProps={{ readOnly: true }}
            rules={{
              required: REQUIRED_FIELD_ERROR_MESSAGE,
              validate: (value: string) => isPostalCodeValid(value) || t('patientProfile.errors.zipDigits'),
            }}
            data-testid={dataTestIds.contactInformationContainer.zip}
          />
        </Box>
      </Row>
      <Row label={t('patientProfile.fields.patientEmail')} required={true}>
        <FormTextField
          id={contact.email.key}
          name={contact.email.key}
          data-testid={dataTestIds.contactInformationContainer.patientEmail}
          control={control}
          disabled={isLoading}
          InputProps={{ readOnly: true }}
          rules={{
            required: REQUIRED_FIELD_ERROR_MESSAGE,
            pattern: {
              value: emailRegex,
              message: t('patientProfile.errors.emailFormat'),
            },
          }}
        />
      </Row>
      <Row label={t('patientProfile.fields.patientMobile')} required={true}>
        <FormTextField
          id={contact.phone.key}
          name={contact.phone.key}
          control={control}
          disabled={isLoading}
          inputProps={{ mask: '(000) 000-0000', readOnly: true }}
          InputProps={{
            inputComponent: InputMask as any,
          }}
          rules={{
            required: REQUIRED_FIELD_ERROR_MESSAGE,
            validate: (value: string) => isPhoneNumberValid(value) || t('patientProfile.errors.phoneDigits'),
          }}
          data-testid={dataTestIds.contactInformationContainer.patientMobile}
        />
      </Row>
    </Section>
  );
};
